import axios from "axios";
import { rutas } from "../rutas/rutas";

const NFT_SYMBOL = "NFTHUESO";
const HIVE_ENGINE_ID = "ssc-mainnet-hive";
const MAX_NFTS_PER_OPERATION = 50;

const ALLOWED_SELL_TOKENS = [
  "VIPHUESO",
  "SWAP.HIVE",
  "SWAP.HBD",
  "STARPRO",
  "HUESO",
];

function getHiveUsername() {
  const storedUser = sessionStorage.getItem("user");

  if (!storedUser) {
    console.error("No user was found in sessionStorage.");
    return null;
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    return parsedUser?.user || null;
  } catch (error) {
    console.error("The stored user could not be read.", error);
    return null;
  }
}

function normalizeNftIds(nfts) {
  const nftList = Array.isArray(nfts) ? nfts : [nfts];

  return [
    ...new Set(
      nftList
        .filter((nftId) => nftId !== null && nftId !== undefined)
        .map((nftId) => String(nftId).trim())
        .filter((nftId) => nftId !== "")
    ),
  ];
}

function isKeychainAvailable() {
  if (!window?.hive_keychain) {
    console.error("Hive Keychain is not installed or is not available.");
    return false;
  }

  return true;
}

export const signin = (username, handleLogin, parametros) => {
  try {
    if (!isKeychainAvailable()) return;

    window.hive_keychain.requestHandshake(() => {
      finishSignin(username, handleLogin, parametros);
    });
  } catch (error) {
    console.error("Unable to connect to Hive Keychain.", error);
  }
};

function finishSignin(username, handleLogin, parametros = "") {
  const message = Date.now().toString();

  try {
    window.hive_keychain.requestSignBuffer(
      username,
      message,
      "Posting",
      function (response) {
        if (response?.error || !response?.success) {
          console.error("The login signature could not be completed.", response);
          return;
        }

        const signedUsername = response?.data?.username;

        if (!signedUsername) {
          console.error("Hive Keychain did not return a valid username.");
          return;
        }

        axios
          .post(rutas.login, {
            nombre: signedUsername,
            key: response.result,
            message,
            parametros,
          })
          .then((res) => {
            const token = res.data?.token;

            if (!token) {
              console.error("The server did not return an access token.");
              return;
            }

            const secureCookie =
              window.location.protocol === "https:" ? "; Secure" : "";

            document.cookie =
              `token=${token}; Max-Age=${3600 * 12}; Path=/; ` +
              `SameSite=Strict${secureCookie}`;

            handleLogin(res.data);
          })
          .catch((error) => {
            console.error("Login failed.", error);
          });
      }
    );
  } catch (error) {
    console.error("An error occurred during the login signature.", error);
  }
}

export function buy_nfts(
  nfts,
  price,
  priceSymbol,
  message = "Buy NFT"
) {
  if (!isKeychainAvailable()) return;

  const nftIds = normalizeNftIds(nfts);
  const username = getHiveUsername();

  if (!username) {
    console.error("No valid user was found to complete the purchase.");
    return;
  }

  if (nftIds.length === 0) {
    console.error("No NFT was provided for purchase.");
    return;
  }

  if (nftIds.length > MAX_NFTS_PER_OPERATION) {
    console.error(
      `Only ${MAX_NFTS_PER_OPERATION} NFTs are allowed per operation.`
    );
    return;
  }

  const priceText = String(price ?? "").trim();
  const priceNumber = Number(priceText);

  if (!priceText || !Number.isFinite(priceNumber) || priceNumber <= 0) {
    console.error("The purchase price must be greater than zero.");
    return;
  }

  if (!priceSymbol) {
    console.error("No payment symbol was provided.");
    return;
  }

  const buyMultipleNft = {
    contractName: "nftmarket",
    contractAction: "buy",
    contractPayload: {
      symbol: NFT_SYMBOL,
      nfts: nftIds,
      expPrice: priceText,
      expPriceSymbol: String(priceSymbol),
      marketAccount: "kingofduels",
    },
  };

  window.hive_keychain.requestCustomJson(
    username,
    HIVE_ENGINE_ID,
    "Active",
    JSON.stringify(buyMultipleNft),
    message,
    function (response) {
      console.log("NFT purchase response:", response);

      if (response?.success && response?.result) {
        console.log(`${nftIds.length} NFT(s) purchased successfully.`);
      } else {
        console.error(
          "The purchase could not be completed.",
          response?.message || response?.error || response
        );
      }
    }
  );
}

export function cancel_nft_sell(nfts) {
  if (!isKeychainAvailable()) return;

  const nftIds = normalizeNftIds(nfts);
  const username = getHiveUsername();

  if (!username) {
    console.error("No valid user was found to cancel the listing.");
    return;
  }

  if (nftIds.length === 0) {
    console.error("No NFT was provided for cancellation.");
    return;
  }

  if (nftIds.length > MAX_NFTS_PER_OPERATION) {
    console.error(
      `Only ${MAX_NFTS_PER_OPERATION} NFTs are allowed per operation.`
    );
    return;
  }

  const cancelNftListing = {
    contractName: "nftmarket",
    contractAction: "cancel",
    contractPayload: {
      symbol: NFT_SYMBOL,
      nfts: nftIds,
    },
  };

  const confirmationMessage =
    nftIds.length === 1
      ? "Cancel this NFT sell listing?"
      : `Cancel ${nftIds.length} NFT sell listings?`;

  window.hive_keychain.requestCustomJson(
    username,
    HIVE_ENGINE_ID,
    "Active",
    JSON.stringify(cancelNftListing),
    confirmationMessage,
    function (response) {
      console.log("Sell cancellation response:", response);

      if (response?.success && response?.result) {
        console.log(`${nftIds.length} listing(s) cancelled successfully.`);
      } else {
        console.error(
          "The sell listing could not be cancelled.",
          response?.message || response?.error || response
        );
      }
    }
  );
}

export function sell_nft(
  nfts,
  price,
  priceSymbol,
  message = ""
) {
  if (!isKeychainAvailable()) return;

  const nftIds = normalizeNftIds(nfts);
  const username = getHiveUsername();

  if (!username) {
    console.error("No valid user was found to complete the sale.");
    return;
  }

  if (nftIds.length === 0) {
    console.error("You must select at least one NFT.");
    return;
  }

  if (nftIds.length > MAX_NFTS_PER_OPERATION) {
    console.error(
      `You can sell a maximum of ${MAX_NFTS_PER_OPERATION} NFTs per operation.`
    );
    return;
  }

  const priceText = String(price ?? "").trim();
  const priceNumber = Number(priceText);

  if (!priceText || !Number.isFinite(priceNumber) || priceNumber <= 0) {
    console.error("The price must be a number greater than zero.");
    return;
  }

  const selectedToken = String(priceSymbol ?? "").trim().toUpperCase();

  if (!ALLOWED_SELL_TOKENS.includes(selectedToken)) {
    console.error(
      `Unsupported token. Use one of these options: ${ALLOWED_SELL_TOKENS.join(
        ", "
      )}.`
    );
    return;
  }

  const sellNftPayload = {
    contractName: "nftmarket",
    contractAction: "sell",
    contractPayload: {
      symbol: NFT_SYMBOL,

      // One or up to 50 selected NFT IDs.
      nfts: nftIds,

      // This price is applied individually to each NFT.
      price: priceText,
      priceSymbol: selectedToken,

      marketAccount: "mv-deposito",
      fee: 500,
    },
  };

  const confirmationMessage =
    message ||
    (nftIds.length === 1
      ? `Sell NFT for ${priceText} ${selectedToken}`
      : `Sell ${nftIds.length} NFTs for ${priceText} ${selectedToken} each`);

  window.hive_keychain.requestCustomJson(
    username,
    HIVE_ENGINE_ID,
    "Active",
    JSON.stringify(sellNftPayload),
    confirmationMessage,
    function (response) {
      console.log("NFT sale response:", response);

      if (response?.success && response?.result) {
        console.log(
          `${nftIds.length} NFT(s) submitted to the market successfully.`
        );
      } else {
        console.error(
          "The sale could not be completed.",
          response?.message || response?.error || response
        );
      }
    }
  );
}
